import { isArray } from 'lodash';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import { ShortUrl } from '@air/api';
import { ShortUrlObjectType } from '@air/api/types';
import { MISSING_OR_INVALID_SHORT_URL_TOKEN, matchesAirror } from '@air/errors';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PublicBoard, type PublicBoardProps } from '~/components/Layouts/PublicBoardLayout/PublicBoard';
import {
  buildPublicCanvasInitialData,
  PublicCanvas,
  type PublicCanvasProps,
} from '~/components/Layouts/PublicCanvasLayout/PublicCanvas';
import { PublicClip, type PublicClipProps } from '~/components/Layouts/PublicClipLayout/PublicClip';
import { NotFound } from '~/components/NotFound/NotFound';
import { ShareLinkPasscodeGate } from '~/components/PasswordPage/ShareLinkPasscodeGate';
import { PublicBoardForcedTheme } from '~/components/v2/components/board/PublicBoardForcedTheme';
import { PublicShowcaseView } from '~/components/v2/components/showcase/PublicShowcaseView';
import { useShowUI2Page } from '~/components/v2/hooks/useShowUI2Page';
import { PublicBoardLayout } from '~/components/v2/layouts/PublicBoardLayout';
import { createShortUrlBoardRoute, createShortUrlClipInBoardRoute } from '~/constants/routes';
import { type NextPageWithLayout } from '~/pages/_app';
import { ReCaptchaProvider } from '~/providers/ReCaptchaProvider';
import {
  type ClipSeoData,
  convertClipToSeoData,
  fetchPublicBoardShareData,
  fetchPublicClipShareData,
  fetchPublicShowcaseShareData,
} from '~/swr-hooks/shortUrl/utils';
import { getShareLinkForcedTheme } from '~/utils/getShareLinkForcedTheme';
import { getBoardIdFromPath, getClipIdFromPath } from '~/utils/PathUtils';
import {
  isAssetOrClipRoute,
  isBoardRoute,
  isCanvasRoute,
  isNotFoundRoute,
  isPasscodeProtected,
  isShowcaseRoute,
  type NotFoundProps,
  type PasscodeRequiredProps,
  type PublicShowcaseRouteProps,
  type ShortUrlPageProps,
} from '~/utils/shareUrl';

/** staticProps are props that come in once from the values from getStaticProps down below. They never change after the initial render */
const ShortUrlPage: NextPageWithLayout<ShortUrlPageProps> = (staticProps) => {
  const { replace, query } = useRouter();
  const { showUI2 } = useShowUI2Page();

  // A NotFound payload still carries a valid-looking shortId, so it satisfies the prefix-based
  // route guards below while having no initialData — it must be excluded before them.
  if (isNotFoundRoute(staticProps)) {
    return <NotFound />;
  }

  // Each route type below renders one of two shapes: a passcode-protected link — whose ISR props
  // carry no data — mounts a ShareLinkPasscodeGate with a type-specific fetchVerifiedData (the
  // client-side twin of that route's getStaticProps case), and an unprotected link renders its tree
  // straight from ISR props, no gate in the tree at all. The route dispatch happens exactly once,
  // here on the shortId prefix: each fetcher validates that the resolved object matches the prefix
  // instead of re-dispatching on it.
  if (isBoardRoute(staticProps)) {
    // ReCaptchaProvider lives here, above the showUI2 swap below, and intentionally NOT inside
    // PublicBoardLayout/PublicBoard. showUI2 (= isAboveMediumScreen) swaps those two layouts when the
    // viewport crosses the medium breakpoint at runtime; if each layout mounted its own provider, that
    // swap would unmount/remount GoogleReCaptchaProvider mid-flight and crash Google's reCAPTCHA script
    // ("Cannot convert undefined or null to object"). One provider above the swap stays mounted across it.
    // Once showUI2 / the legacy PublicBoard path is gone and a single board layout remains, this hoist
    // can be removed and the provider moved back into PublicBoardLayout.
    // PublicBoardForcedTheme sits above the swap for the same stay-mounted reason — one mount themes
    // both trees at the document level, from the same resolved data the trees render (so
    // passcode-verified links are themed too).
    const { shortId } = staticProps;

    if (isPasscodeProtected(staticProps)) {
      return (
        <ShareLinkPasscodeGate
          shortId={shortId}
          fetchVerifiedData={async () => {
            const res = await ShortUrl.getByShortId(shortId);

            if (res.type !== ShortUrlObjectType.board) {
              throw new Error(`Board share link ${shortId} resolved to a ${res.type}`);
            }

            // boardId will be empty if the user opened /a/{shortId} — fall back to the root board.
            const path = isArray(query.slug) ? query.slug.join('/') : '';
            const boardId = getBoardIdFromPath(path) || res.data.board.id;
            const clipId = getClipIdFromPath(path);

            const data = await fetchPublicBoardShareData({ shortId, boardId, response: res });

            // Normalize the URL to the full board path (getStaticProps redirects unverified visitors
            // the same way; a passcode-gated load never went through that redirect).
            if (clipId) {
              await replace(createShortUrlClipInBoardRoute(shortId, boardId, clipId));
            } else {
              await replace(createShortUrlBoardRoute(shortId, boardId));
            }

            return data;
          }}
        >
          {(initialBoardData) => {
            const { board, workspace, permissions, presentationSettings, requireAccount, isOnPaidPlan } =
              initialBoardData;

            return (
              <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
                <PublicBoardForcedTheme colorMode={presentationSettings?.colorMode} />
                {showUI2 ? (
                  <PublicBoardLayout
                    board={board}
                    workspace={workspace}
                    permissions={permissions}
                    presentationSettings={presentationSettings}
                    requireAccount={requireAccount}
                    initialClipData={staticProps.initialClipData}
                    shortIdProps={{
                      shortId,
                      objectType: 'board',
                      objectId: board.id,
                      presentationSettings,
                      isOnPaidPlan,
                    }}
                  />
                ) : (
                  <PublicBoard {...staticProps} initialData={initialBoardData} />
                )}
              </ReCaptchaProvider>
            );
          }}
        </ShareLinkPasscodeGate>
      );
    }

    const { board, workspace, permissions, presentationSettings, requireAccount, isOnPaidPlan } =
      staticProps.initialData;

    return (
      <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
        <PublicBoardForcedTheme colorMode={presentationSettings?.colorMode} />
        {showUI2 ? (
          <PublicBoardLayout
            board={board}
            workspace={workspace}
            permissions={permissions}
            presentationSettings={presentationSettings}
            requireAccount={requireAccount}
            initialClipData={staticProps.initialClipData}
            shortIdProps={{
              shortId,
              objectType: 'board',
              objectId: board.id,
              presentationSettings,
              isOnPaidPlan,
            }}
          />
        ) : (
          <PublicBoard {...staticProps} initialData={staticProps.initialData} />
        )}
      </ReCaptchaProvider>
    );
  }

  if (isCanvasRoute(staticProps)) {
    const { shortId } = staticProps;

    if (isPasscodeProtected(staticProps)) {
      return (
        <ShareLinkPasscodeGate
          shortId={shortId}
          fetchVerifiedData={async () => {
            const res = await ShortUrl.getByShortId(shortId);

            if (res.type !== ShortUrlObjectType.canvas) {
              throw new Error(`Canvas share link ${shortId} resolved to a ${res.type}`);
            }

            return buildPublicCanvasInitialData(res);
          }}
        >
          {(initialCanvasData) => <PublicCanvas shortId={shortId} initialData={initialCanvasData} />}
        </ShareLinkPasscodeGate>
      );
    }

    return <PublicCanvas shortId={shortId} initialData={staticProps.initialData} />;
  }

  if (isAssetOrClipRoute(staticProps)) {
    const { shortId } = staticProps;

    if (isPasscodeProtected(staticProps)) {
      return (
        <ShareLinkPasscodeGate
          shortId={shortId}
          fetchVerifiedData={async () => {
            const res = await ShortUrl.getByShortId(shortId);

            // `objectType: 'asset'` resolves to a clip-shaped payload on the wire
            // (see `GetByShortIDAssetResponse`), so it renders through the same path as a clip.
            if (res.type !== ShortUrlObjectType.asset && res.type !== ShortUrlObjectType.clip) {
              throw new Error(`Clip share link ${shortId} resolved to a ${res.type}`);
            }

            return fetchPublicClipShareData({ shortId, response: res });
          }}
        >
          {(initialClipData) => <PublicClip {...staticProps} initialData={initialClipData} />}
        </ShareLinkPasscodeGate>
      );
    }

    return <PublicClip {...staticProps} initialData={staticProps.initialData} />;
  }

  if (isShowcaseRoute(staticProps)) {
    // A collection route ALWAYS mounts this ONE collection tree (FOUND-7 composition). Both the
    // collection root (/a/m<shortId>) and a board reached inside it (/a/m<shortId>/b/<boardId>, via
    // cold deep-link or shallow drill-in) render through this single PublicShowcaseLayout — the
    // board renders INSIDE the tree via PublicShowcasePage, which reads the /b/<boardId> segment
    // through useCurrentBoardIdSelector, and PublicShowcaseBoardView fetches the board's own
    // record. This replaces the old second-PublicBoardLayout swap (objectType='collection' threaded
    // down, its own ReCaptchaProvider, and page-level board state bridging shallow routing): that
    // dual tree was the root of the leaf-level "am I in a collection?" checks. reCAPTCHA is owned by
    // the collection layout, so no wrapper is needed here.
    const { shortId } = staticProps;

    if (isPasscodeProtected(staticProps)) {
      return (
        <ShareLinkPasscodeGate
          shortId={shortId}
          fetchVerifiedData={async () => {
            const res = await ShortUrl.getByShortId(shortId);

            if (res.type !== ShortUrlObjectType.collection) {
              throw new Error(`Collection share link ${shortId} resolved to a ${res.type}`);
            }

            // Only the collection's own data is fetched — a /b/{boardId} deep-link needs nothing extra
            // here, because the collection tree always mounts and the drilled board view fetches its
            // own record (see PublicShowcaseBoardView).
            return fetchPublicShowcaseShareData({ shortId, response: res });
          }}
        >
          {({
            collection,
            permissions,
            presentationSettings,
            presentationContent,
            presentationPages,
            requireAccount,
            isOnPaidPlan,
          }) => (
            <PublicShowcaseView
              collection={collection}
              permissions={permissions}
              presentationSettings={presentationSettings}
              presentationContent={presentationContent}
              presentationPages={presentationPages}
              requireAccount={requireAccount}
              isOnPaidPlan={isOnPaidPlan}
              shortId={shortId}
            />
          )}
        </ShareLinkPasscodeGate>
      );
    }

    const {
      collection,
      permissions,
      presentationSettings,
      presentationContent,
      presentationPages,
      requireAccount,
      isOnPaidPlan,
    } = staticProps.initialData;

    return (
      <PublicShowcaseView
        collection={collection}
        permissions={permissions}
        presentationSettings={presentationSettings}
        presentationContent={presentationContent}
        presentationPages={presentationPages}
        requireAccount={requireAccount}
        isOnPaidPlan={isOnPaidPlan}
        shortId={shortId}
      />
    );
  }

  return <NotFound />;
};

// Forces the theme a share link authored via presentationSettings.colorMode — derived from this
// page's own ISR props so `_app` can stamp it before first paint (same idiom as `getLayout`).
ShortUrlPage.getForcedTheme = getShareLinkForcedTheme;

export default ShortUrlPage;

export const getStaticProps: GetStaticProps<ShortUrlPageProps, { slug?: string[] }> = async ({ params }) => {
  /** If there is no slug param, it's an invalid path. */
  if (!params?.slug) {
    const props: NotFoundProps = {
      shortId: undefined,
      notFound: true,
    };

    return {
      revalidate: 1,
      props,
    };
  }

  const [shortId] = params.slug;
  const path = params.slug.join('/');
  const boardId = getBoardIdFromPath(path) || null; // to fix `undefined` cannot be serialized as JSON error
  const clipId = getClipIdFromPath(path) || null; // to fix `undefined` cannot be serialized as JSON error

  /** If there is no shortId, it's an invalid path */
  if (!shortId) {
    const props: NotFoundProps = {
      shortId: undefined,
      notFound: true,
    };

    return {
      revalidate: 1,
      props,
    };
  }

  try {
    /**
     * Resolve the link's own object FIRST — the shortId is the route's object pointer (a share URL
     * is `/a/{shortId}[/b/{boardId}][/c/{clipId}]`; boards and clips are the only objects that also
     * appear as path segments). Every object type then follows the same architecture: the route's
     * object is fetched fully, deep-linked descendants are fetched per type, and the props are an
     * honest discriminated shape per type. This call is also the passcode gate — on a protected
     * link it throws before any descendant fetch runs.
     */
    const res = await ShortUrl.getByShortId(shortId);

    // A board segment on a link type that can't contain boards (clip/asset/canvas) is a nonsense
    // URL — same 404 those URLs produced when the old board-first branch failed to resolve the
    // segment as a descendant.
    if (boardId && res.type !== ShortUrlObjectType.board && res.type !== ShortUrlObjectType.collection) {
      const props: NotFoundProps = {
        shortId,
        notFound: true,
      };

      return {
        props,
        revalidate: 1,
      };
    }

    switch (res.type) {
      case ShortUrlObjectType.board: {
        /**
         * A board link visited without a board segment redirects to its full board path; the
         * redirected request re-enters here with the boardId present.
         */
        if (!boardId) {
          return {
            revalidate: 1,
            redirect: {
              destination: createShortUrlBoardRoute(shortId, res.data.board.id),
              permanent: true,
            },
          };
        }

        // The route's board data, plus the SEO-only head seed for a /c/{clipId} deep-link — the
        // clip itself renders async inside the board tree.
        const [initialData, clipResult] = await Promise.all([
          fetchPublicBoardShareData({ shortId, boardId, response: res }),
          clipId ? ShortUrl.getBoardClip(shortId, clipId).catch(() => null) : Promise.resolve(null),
        ]);

        const initialClipData: ClipSeoData | undefined = clipResult ? convertClipToSeoData(clipResult.clip) : undefined;

        const props: PublicBoardProps = {
          shortId,
          boardId,
          initialData,
          ...(initialClipData ? { initialClipData } : {}),
        };

        return {
          props,
          revalidate: 1,
        };
      }

      // `objectType: 'asset'` resolves to a clip-shaped payload on the wire
      // (see `GetByShortIDAssetResponse`), so it renders through the same path as a clip.
      case ShortUrlObjectType.asset:
      case ShortUrlObjectType.clip: {
        const props: PublicClipProps = {
          shortId,
          initialData: await fetchPublicClipShareData({ shortId, response: res }),
        };

        return { props, revalidate: 1 };
      }

      case ShortUrlObjectType.collection: {
        // Only the collection's own data ships — deep-linked descendants (/b/{boardId},
        // /c/{clipId}) contribute NO SSR payload. The collection tree always mounts and the drilled
        // board/clip fetch their own records inside it (see PublicShowcaseBoardView). Known gap,
        // deliberate: collection deep-links have no board/clip SEO head seeds because the collection
        // page has no head-tag consumer for them yet — the collection's own tags render.
        const props: PublicShowcaseRouteProps = {
          shortId,
          initialData: await fetchPublicShowcaseShareData({ shortId, response: res }),
        };

        return { props, revalidate: 1 };
      }

      case ShortUrlObjectType.canvas: {
        const props: PublicCanvasProps = {
          shortId,
          initialData: buildPublicCanvasInitialData(res),
        };

        return { props, revalidate: 1 };
      }

      default:
        throw new Error('Unexpected ShortUrlObject type');
    }
  } catch (error) {
    /**
     * If we can't fetch the link's data cause they don't have the short url passcode token
     */
    if (matchesAirror(error, MISSING_OR_INVALID_SHORT_URL_TOKEN)) {
      const props: PasscodeRequiredProps = {
        shortId,
        boardId,
        clipId,
        passcodeRequired: true,
      };

      return {
        revalidate: 1,
        props,
      };
    } else {
      reportErrorToBugsnag({
        error,
        context: 'Uncaught error during ISR of share route.',
        metadata: { data: { shortId } },
      });

      const props: NotFoundProps = {
        shortId,
        notFound: true,
      };

      return {
        props,
        /**
         * This is really important because this forces 404 pages on share links to revalidate in the background.
         * There was a nice disaster created by this prop not being here.
         * @see https://air-labs-team.slack.com/archives/CKE8SK3M1/p1678113628505779
         */
        revalidate: 1,
      };
    }
  }
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [], // We don't want to statically pre-render path. Only the <head> element for the page on request.
  fallback: 'blocking', // https://nextjs.org/docs/basic-features/data-fetching#fallback-blocking
});
